import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MdChevronLeft, MdSend, MdChat, MdCameraAlt, MdHistory, MdAddComment, MdPushPin, MdOutlineContactless, MdUploadFile, MdStopCircle,
  MdMic, MdMicOff, MdVolumeUp, MdVolumeOff,
  MdContentCopy, MdCheck, MdThumbUpOffAlt, MdThumbUp, MdThumbDownOffAlt, MdThumbDown,
  MdRecordVoiceOver, MdPeople, MdClose,
} from 'react-icons/md';
import { useProfile } from '../contexts/ProfileContext';
import { useApp } from '../contexts/AppContext';
import { useTTS } from '../hooks/useTTS';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { SUMMARY_THRESHOLD, SUMMARY_KEEP_RECENT, TITLE_MILESTONES, TITLE_INTERVAL } from '../constants/chat';
import { sendChatMessage, filterProfile, compactHistory, fetchInsights, runInsightsAgents, extractMemory, getRelevantAssumptions, decideAssumption, authHeaders, previewAction, executeAction, checkScamMessage, getSwipePreferences, streamEMTAgent } from '../services/backendAPI';
import ConversationMode from '../components/ConversationMode';
import ChartWrapper, { parseChartData } from '../components/ChartWrapper';
import SwipeCard, { EMTTriageCard } from '../components/SwipeCards';
import { startSwipeFlow, advanceSwipeFlow, stepAcceptsFreeText } from './swipe/chatSwipeFlow';
import { startEMTFlow, advanceEMTFlow } from './swipe/chatEMTFlow';
import { startAgentEMTFlow, advanceAgentEMTFlow } from './swipe/chatAgentEMTFlow';
import txData from '../data/transactions.json';
import chatData from '../data/chat.json';
import { generateId } from '../utils/uuid';
import { captureChatScroll, downloadCanvas } from '../utils/chatScreenshot';

const transactionsData = txData.transactionsData;
const accountTransactions = txData.accountTransactions;
const initialChatHistory = chatData.initialChatHistory;
const chatSuggestions = chatData.chatSuggestions;

const { MdAutoAwesome } = require('react-icons/md');
const chatCarouselData = chatData.chatCarouselData.map(card => ({
  ...card,
  icon: <MdAutoAwesome />,
}));

// ── Context window management ─────────────────────────────────────────────────
// Strategy for compacting conversation history when approaching the token limit.
// 'truncate' — free, instant, loses old turns (default).
// 'llm'      — calls /api/compact; slower but produces a readable summary.
const COMPACTION_STRATEGY = 'truncate';
const KEEP_LAST_TURNS = 6; // turns kept verbatim during compaction

// Known context window sizes (tokens) per model. Conservative for free-tier models.
// Groq free tier has per-request payload caps that make 128K unusable in practice.
const MODEL_CONTEXT_WINDOWS = {
  'command-r-plus-08-2024': 128000, 'command-r-08-2024': 128000,
  'command-r7b-12-2024': 128000, 'command-a-03-2025': 256000,
  'claude-3-haiku-20240307': 200000, 'claude-haiku-4-5-20251001': 200000,
  'claude-sonnet-4-6': 200000, 'claude-sonnet-3-5-20241022': 200000,
  'gpt-4o-mini': 128000, 'gpt-4o': 128000, 'gpt-3.5-turbo': 16385,
  'o3-mini': 200000, 'o1-mini': 128000,
  'gemini-2.5-flash-lite': 1000000, 'gemini-2.5-flash': 1000000, 'gemini-3.1-flash-lite': 1000000,
  'llama-3.3-70b-versatile': 8192, 'llama-3.1-8b-instant': 8192,
  'llama-4-scout': 8192, 'qwen3-32b': 8192,
  'nvidia/nemotron-nano-9b-v2:free': 8192, 'nvidia/nemotron-3-super-120b-a12b:free': 8192,
  'google/gemma-4-31b-it:free': 8192, 'openai/gpt-oss-120b:free': 8192,
  'qwen3.5:4b': 32768, 'qwen3.5:7b': 32768, 'qwen3.5:14b': 32768,
  'phi4': 16384, 'phi4-mini': 8192, 'llama3.1:8b': 8192, 'llama3.2:3b': 4096,
};
const DEFAULT_CONTEXT_WINDOW = 8192;

// Rough token estimator — 1 token ≈ 4 chars. Good enough for budget checks.
function estimateTokens(text) {
  return Math.ceil((text || '').length / 4);
}

// Dedup key for accumulated transactions — prevents the same row appearing twice
// if two consecutive questions return overlapping date ranges.
const txnKey = t => `${t.date}|${t._acct}|${t.amount}|${(t.description || '').slice(0, 20)}`;

// Returns true when the message is likely asking about specific transactions,
// triggering the server-side filter agent before the main LLM call.
function needsDetailedContext(msg) {
  return /\b(spent|spending|charges?|purchases?|transactions?|list|show me|how much|how many|in\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)|last\s+(month|week|quarter)|this\s+(month|week))\b/i.test(msg);
}

function callBackendAPI(userMessage, conversationHistory, provider = 'cohere', model = null, profileData = null, signal = null, noThink = false, customSystemPrompt = null, username = null, sessionId = null, sessionSummary = null, filteredTransactions = [], filteredStats = null) {
  return sendChatMessage({ message: userMessage, history: conversationHistory || [], provider, model, profileData, signal, noThink, customSystemPrompt, username, sessionId, sessionSummary, filteredTransactions, filteredStats });
}

// Kit pattern: "9:30 AM" — passed in on each message; rendered below the bubble.
const nowLabel = () => new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

// Strip markdown / code fences so the spoken or copied version is clean text.
const stripMarkdown = (s = '') =>
  s.replace(/```[\s\S]*?```/g, '').replace(/[*_`#>]/g, '').trim();

// Quick + cheap conversation-title from the user's first message — local,
// no backend call. Trims to ~60 chars at a word boundary, sentence-cased.
const titleFromMessage = (raw) => {
  const cleaned = stripMarkdown(raw);
  if (!cleaned) return null;
  let t = cleaned.slice(0, 60);
  if (cleaned.length > 60) {
    const lastSpace = t.lastIndexOf(' ');
    if (lastSpace > 30) t = t.slice(0, lastSpace);
    t = t + '…';
  }
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const ALL_INSIGHT_AGENTS = ['anomalies', 'subscriptions', 'budgetPulse', 'narrative', 'spendDigest', 'healthScore'];

// Per-bot-message action toolbar: copy + thumbs up / down. Copy uses the
// async clipboard API and falls back silently (we just don't update state
// if it's unavailable). Feedback fires a fire-and-forget POST — UI never
// waits or shows network state.
const MessageActions = ({ text, onFeedback }) => {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState(null); // null | 'like' | 'dislike'

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(stripMarkdown(text));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard not available — silent */ }
  };

  const handleFeedback = (type) => {
    if (feedback === type) return;
    setFeedback(type);
    if (onFeedback) onFeedback(type);
  };

  return (
    <div className="chat-msg-actions">
      <button
        type="button"
        className={`chat-msg-action${copied ? ' is-success' : ''}`}
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy reply'}
        title={copied ? 'Copied' : 'Copy reply'}
      >
        {copied ? <MdCheck size={14} /> : <MdContentCopy size={14} />}
      </button>
      <button
        type="button"
        className={`chat-msg-action${feedback === 'like' ? ' is-active' : ''}`}
        onClick={() => handleFeedback('like')}
        aria-label="Helpful"
        title="Helpful"
        aria-pressed={feedback === 'like'}
      >
        {feedback === 'like' ? <MdThumbUp size={14} /> : <MdThumbUpOffAlt size={14} />}
      </button>
      <button
        type="button"
        className={`chat-msg-action${feedback === 'dislike' ? ' is-active is-danger' : ''}`}
        onClick={() => handleFeedback('dislike')}
        aria-label="Not helpful"
        title="Not helpful"
        aria-pressed={feedback === 'dislike'}
      >
        {feedback === 'dislike' ? <MdThumbDown size={14} /> : <MdThumbDownOffAlt size={14} />}
      </button>
    </div>
  );
};

const AssumptionConfirmCard = ({ prompts, onDecide }) => {
  if (!prompts?.length) return null;
  return (
    <div style={{
      marginTop: 8, padding: '10px 12px', borderRadius: 12,
      border: '1px solid #d8e6da', background: '#f4faf5', maxWidth: 460,
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#1c6b2e', marginBottom: 8 }}>
        IVA wants to confirm a few things
      </div>
      {prompts.map(p => (
        <div key={p.id} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 10, padding: '4px 0',
        }}>
          <span style={{ fontSize: 13, color: '#2a2a2a' }}>{p.text}?</span>
          {p.resolved ? (
            <span style={{ fontSize: 12, color: '#6a6a6a', whiteSpace: 'nowrap' }}>
              {p.resolved === 'confirm' ? '✓ Yes' : '✓ No'}
            </span>
          ) : (
            <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button onClick={() => onDecide(p, 'confirm')} style={{
                border: 'none', borderRadius: 14, padding: '3px 12px', fontSize: 12,
                fontWeight: 600, cursor: 'pointer', background: '#1c8a3a', color: '#fff',
              }}>Yes</button>
              <button onClick={() => onDecide(p, 'dismiss')} style={{
                border: '1px solid #cbd5cd', borderRadius: 14, padding: '3px 12px', fontSize: 12,
                cursor: 'pointer', background: '#fff', color: '#555',
              }}>No</button>
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

// Detect a "create a savings goal" intent in the user's message and pull out the
// title, target amount, and (optional) monthly contribution. Returns null when the
// message isn't a goal request, so nothing renders in the common case.
export function parseGoalIntent(text) {
  if (!text) return null;
  const t = String(text);
  if (!/\b(sav(e|ing)|goal|put aside|set aside|save up)\b/i.test(t)) return null;

  const toNum = (raw, unit) => {
    let n = Number(String(raw).replace(/,/g, ''));
    if (!(n > 0)) return null;
    if (unit && /^k$|thousand/i.test(unit)) n *= 1000;
    return Math.round(n);
  };

  // Monthly contribution, e.g. "$250/mo", "250 a month", "put away 300 monthly".
  let monthly = null;
  const mMatch = t.match(/\$?\s?([\d,]+(?:\.\d+)?)\s?(k|thousand)?\s*(?:\/\s*mo\b|\/\s*month\b|a month|per month|monthly|each month)/i);
  if (mMatch) monthly = toNum(mMatch[1], mMatch[2]);

  // Target amount — the first dollar-ish number that ISN'T the monthly figure.
  let target = null;
  const amtRe = /\$?\s?([\d,]+(?:\.\d+)?)\s?(k|thousand)?/gi;
  let am;
  while ((am = amtRe.exec(t)) !== null) {
    const val = toNum(am[1], am[2]);
    if (val == null) continue;
    if (monthly != null && val === monthly) continue; // skip the monthly amount
    target = val;
    break;
  }
  if (target == null) return null;

  // Title — prefer "for <thing>", else "toward/towards <thing>", else a default.
  let title = null;
  const forMatch = t.match(/\b(?:for|toward|towards|to buy|on)\s+(?:a |an |my |the )?([a-z][a-z0-9 '-]{2,40})/i);
  if (forMatch) {
    title = forMatch[1].trim().replace(/[.,!?].*$/, '').trim();
    // Drop a trailing amount clause that sometimes gets captured.
    title = title.replace(/\s+\$?[\d,]+.*$/, '').trim();
  }
  if (!title) title = 'Savings goal';
  // Title-case the first letter for display.
  title = title.charAt(0).toUpperCase() + title.slice(1);

  return { title, target, monthly };
}

// Confirm-gated card that turns a detected goal intent into a durable goal. It
// previews the action (server validates), shows a before/after summary, and only
// on Confirm executes it — mirroring the pay_down_credit_card confirm UX. On
// success it hands the fresh goals array back so the Goals screen updates.
function GoalConfirmCard({ sessionId, goal, onCreated }) {
  const [state, setState] = useState('idle'); // idle | working | done | error | cancelled
  const [message, setMessage] = useState('');

  const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

  const confirm = async () => {
    if (!sessionId) { setState('error'); setMessage('No active session.'); return; }
    setState('working');
    const pv = await previewAction(sessionId, 'create_goal', goal);
    if (!pv?.ok) { setState('error'); setMessage(pv?.reason || "Couldn't create that goal."); return; }
    const res = await executeAction(sessionId, 'create_goal', goal);
    if (res?.ok) {
      setState('done');
      setMessage(pv.summary);
      onCreated?.(res.goals || []);
    } else {
      setState('error');
      setMessage(res?.reason || "Couldn't create that goal.");
    }
  };

  if (state === 'cancelled') return null;

  return (
    <div style={{
      marginTop: 8, border: '1px solid #cbd5cd', borderRadius: 12,
      padding: '10px 12px', background: '#f6faf7', maxWidth: 360,
    }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-dark, #14311d)', marginBottom: 4 }}>
        Create this goal?
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-medium, #3a4a40)', marginBottom: 8 }}>
        <strong>{goal.title}</strong> — save {money(goal.target)}
        {goal.monthly ? ` at ${money(goal.monthly)}/mo` : ''}
      </div>
      {state === 'done' ? (
        <div style={{ fontSize: 12, color: '#008a0d', fontWeight: 700 }}>Added to your Goals ✓</div>
      ) : state === 'error' ? (
        <div style={{ fontSize: 12, color: 'var(--td-red)' }}>{message}</div>
      ) : (
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={confirm} disabled={state === 'working'} style={{
            border: 'none', borderRadius: 14, padding: '4px 14px', fontSize: 12,
            fontWeight: 600, cursor: 'pointer', background: '#1c8a3a', color: '#fff',
          }}>{state === 'working' ? 'Creating…' : 'Create goal'}</button>
          <button onClick={() => setState('cancelled')} disabled={state === 'working'} style={{
            border: '1px solid #cbd5cd', borderRadius: 14, padding: '4px 14px', fontSize: 12,
            cursor: 'pointer', background: '#fff', color: '#555',
          }}>Not now</button>
        </div>
      )}
    </div>
  );
}

// ── Advisor escalation CTA ──────────────────────────────────────────────────
// IVA appends the bare marker [ADVISOR_CTA] to a reply when the question warrants
// a licensed human (estate/tax/trade execution/regulated advice). We strip the
// marker from the displayed text and show a dismissible "connect" banner instead.
export const ADVISOR_CTA_MARKER = '[ADVISOR_CTA]';

// Sentinel chatSeed value: any screen can call openChatWith(SWIPE_SEED) (the
// existing chat hand-off mechanism) to open chat and kick off the in-chat
// advisor-matching flow directly, instead of sending the seed to the LLM.
export const SWIPE_SEED = '__START_SWIPE__';

// The request that triggered a bot reply is the nearest preceding user message.
// Captured so the advisor booking screen can pre-fill the topic + run online-first
// triage on the actual request text.
export function precedingUserText(history, idx) {
  for (let i = idx - 1; i >= 0; i -= 1) {
    const m = history[i];
    if (m && !m.isBot && typeof m.text === 'string' && m.text.trim()) return m.text;
  }
  return null;
}

export function stripAdvisorCta(text) {
  if (typeof text !== 'string') return { text, hasCta: false, advisorType: null };
  const match = text.match(/\[ADVISOR_CTA(?::(\w+))?\]/);
  if (!match) return { text, hasCta: false, advisorType: null };
  const advisorType = match[1] || null;
  const cleaned = text.replace(/\[ADVISOR_CTA(?::\w+)?\]/, '').replace(/\n{3,}/g, '\n\n').trim();
  return { text: cleaned, hasCta: true, advisorType };
}

// IVA appends [EMT_TRIAGE] when the customer asks about sending an e-Transfer.
// We strip the marker and render the 3-choice triage card (self/agent/advisor).
export const EMT_TRIAGE_MARKER = '[EMT_TRIAGE]';

export function stripEmtTriage(text) {
  if (typeof text !== 'string') return { text, hasTriage: false };
  if (!text.includes(EMT_TRIAGE_MARKER)) return { text, hasTriage: false };
  const cleaned = text.split(EMT_TRIAGE_MARKER).join('').replace(/\n{3,}/g, '\n\n').trim();
  return { text: cleaned, hasTriage: true };
}

const AdvisorCtaCard = ({ onConnect, onDismiss }) => (
  <div className="advisor-cta-card" role="note">
    <MdPeople className="advisor-cta-icon" size={20} />
    <span className="advisor-cta-text">This might be a good time to speak with a TD advisor.</span>
    <button type="button" className="advisor-cta-connect" onClick={onConnect}>Connect</button>
    <button type="button" className="advisor-cta-dismiss" onClick={onDismiss} aria-label="Dismiss">
      <MdClose size={16} />
    </button>
  </div>
);

export const ChatMessage = ({ id, text, isBot, failed, isSummary, onRetry, onPin, isPinned, timestamp, onFeedback, assumptionPrompts, onAssumptionDecision, goalAction, sessionId, onGoalCreated, onNavigate, advisorRequest, onBeginSwipe, swipeCard, onSwipeAnswer, emtCard, onEMTAnswer, onBeginEMT, onBeginAgentEMT, agentEmtCard, onAgentEMTAnswer, swipeActive }) => {
  const [ctaDismissed, setCtaDismissed] = useState(false);
  const [triageDone, setTriageDone] = useState(false);
  const { text: afterCta, hasCta, advisorType } = isBot ? stripAdvisorCta(text) : { text, hasCta: false, advisorType: null };
  const { text: displayText, hasTriage } = isBot ? stripEmtTriage(afterCta) : { text: afterCta, hasTriage: false };
  const showCta = isBot && !failed && hasCta && !ctaDismissed && !swipeActive;
  const showTriage = isBot && !failed && hasTriage && !triageDone;
  if (isSummary) {
    return (
      <div className="chat-summary-divider">
        <div className="chat-summary-label">Earlier conversation summarized</div>
        <div className="chat-summary-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
        </div>
      </div>
    );
  }
  return (
    <div className={`chat-message-container ${isBot ? 'bot-message' : 'user-message'}`}>
      {isBot && <div className="bot-avatar"><MdOutlineContactless /></div>}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isBot ? 'flex-start' : 'flex-end',
        maxWidth: isBot ? '92%' : '78%',
        // Bot wrapper grows to fill the row so flat bubbles + charts get a
        // defined width to render into. User wrapper stays content-shrunk so
        // the green pill bubble hugs short messages.
        flex: isBot ? 1 : undefined,
        minWidth: 0,
      }}>
        {/* Bot replies render flat (no bubble fill) so long answers feel like
            a document. User messages keep the green pill bubble. */}
        <div className={`chat-bubble${failed ? ' failed-bubble' : ''}${isBot ? ' chat-bubble--flat' : ''}`}>
          {isBot ? <ChartWrapper text={displayText} /> : displayText}
          {failed && (
            <button className="retry-button" onClick={onRetry}>
              ↺ Retry
            </button>
          )}
        </div>
        {showCta && (
          <AdvisorCtaCard
            onConnect={() => (onBeginSwipe ? onBeginSwipe(advisorRequest, advisorType) : onNavigate?.('bookAppointment', advisorRequest))}
            onDismiss={() => setCtaDismissed(true)}
          />
        )}
        {showTriage && (
          <EMTTriageCard onAnswer={(choice) => {
            setTriageDone(true);
            if (choice === 'self') onBeginEMT?.();
            else if (choice === 'agent') onBeginAgentEMT?.();
            else if (choice === 'advisor') onBeginSwipe?.('I need help sending an email money transfer (Interac e-Transfer)', 'general');
          }} />
        )}
        {isBot && !failed && swipeCard && (
          <SwipeCard card={swipeCard} sessionId={sessionId} onAnswer={(value, label) => onSwipeAnswer?.(value, label)} />
        )}
        {isBot && !failed && emtCard && (
          <SwipeCard card={emtCard} sessionId={sessionId} onAnswer={(value, label) => onEMTAnswer?.(value, label)} />
        )}
        {isBot && !failed && agentEmtCard && (
          <SwipeCard card={agentEmtCard} sessionId={sessionId} onAnswer={(value, label) => onAgentEMTAnswer?.(value, label)} />
        )}
        {isBot && !failed && (
          <MessageActions text={displayText} onFeedback={onFeedback} />
        )}
        {isBot && !failed && assumptionPrompts?.length > 0 && (
          <AssumptionConfirmCard
            prompts={assumptionPrompts}
            onDecide={(prompt, decision) => onAssumptionDecision?.(id, prompt, decision)}
          />
        )}
        {isBot && !failed && goalAction && (
          <GoalConfirmCard sessionId={sessionId} goal={goalAction} onCreated={onGoalCreated} />
        )}
        {isBot && !failed && onPin && parseChartData(displayText) && (
          <button
            className={`chat-pin-btn${isPinned ? ' pinned' : ''}`}
            onClick={() => onPin({ id, text: displayText, timestamp: new Date().toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) })}
            title={isPinned ? 'Already saved to dashboard' : 'Save chart to dashboard'}
          >
            <MdPushPin style={{ fontSize: 12 }} />
            {isPinned ? 'Saved' : 'Save to Dashboard'}
          </button>
        )}
        {timestamp && (
          <span className="chat-message-time">{timestamp}</span>
        )}
      </div>
    </div>
  );
};

const ChatSuggestion = ({ text, onClick }) => (
  <button className="chat-suggestion-chip" onClick={onClick}>{text}</button>
);

function extractFollowUpQuestions(text) {
  const cleaned = text.replace(/[*_`#>]/g, '');
  const matches = cleaned.match(/[^.!?\n]{10,80}\?/g) || [];
  return matches
    .map(q => q.trim().replace(/^[-•\d.\s]+/, ''))
    .filter(q => q.length >= 10 && q.length <= 80)
    .slice(0, 5);
}

const ChatScreen = ({
  onNavigate, onBack, onClose, isModal = false, onSaveToDashboard, pinnedMessageIds = [], onSaveSession,
  sessionToLoad, activeHistory, onHistoryChange, currentScreenContext,
  // Option A merger: voice as a mode inside ChatScreen.
  // Future Option C: 'mode' will gain a 'hybrid' value rendering voice + transcript.
  initialVoiceMode = false,
  onLog,
  // When another screen hands off to chat with context (e.g. "I want to
  // open a TFSA"), it sets chatSeed. The seeded text auto-sends once on
  // mount so the assistant has a starting point. onSeedConsumed lets App
  // clear the seed so navigating away and back doesn't replay it.
  chatSeed,
  onSeedConsumed,
}) => {
  const { loadedProfileData, setLoadedProfileData } = useProfile();
  const {
    selectedBackend, selectedModel, ollamaNoThink,
    ttsEngine, setTtsEngine, elVoiceId, setElVoiceId, openaiVoiceId, setOpenaiVoiceId, qwenVoiceId, setQwenVoiceId,
    voiceOut, setVoiceOut,
    voiceLogVisible, setVoiceLogVisible,
    composedSystemPrompt,
    summarizePrompt,
    currentUser,
    summarizeProvider,
    sessionId, sessionSummary, loadProfileSession,
    theme,
    simDate,
  } = useApp();

  // 'text' = chat-first, mic optional. 'voice' = voice-first, TTS auto-on.
  // Reserved future: 'hybrid' (voice + transcript split layout) for Option C.
  // eslint-disable-next-line no-unused-vars
  const [mode, setMode] = useState(initialVoiceMode ? 'voice' : 'text');
  // Full-screen ChatGPT-style voice surface. When true, the chat UI is
  // replaced by <ConversationMode>. The underlying useTTS / useVoiceInput
  // / handleSend pipeline is shared, so any turns generated in conversation
  // mode land in the chat history.
  const [conversationMode, setConversationMode] = useState(false);
  const log = (type, message) => onLog && onLog(type, message);

  // Auto-enable TTS when arriving in voice mode; respect manual override after.
  useEffect(() => {
    if (initialVoiceMode && !voiceOut) setVoiceOut(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialVoiceMode]);

  const chatProfilePayload = useMemo(() => {
    if (!loadedProfileData) return null;
    if (loadedProfileData.source === 'default') return { source: 'default', transactionsData, accountTransactions };
    return { source: loadedProfileData.source, profile: loadedProfileData.profile };
  }, [loadedProfileData]);

  // Load profile to server session cache whenever the selected profile changes.
  // After this, chat calls use sessionId + sessionSummary instead of the full JSON.
  useEffect(() => {
    loadProfileSession(chatProfilePayload);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatProfilePayload]);

  const [profileUploadData, setProfileUploadData] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  // When filter agent needs clarification on time range, stores the original question.
  // The next user message is treated as the answer and re-runs the filter before the LLM call.
  const [pendingClarification, setPendingClarification] = useState(null);

  // Accumulated filtered transactions across turns — grows as filter agent returns results.
  // Deduped by date|account|amount|description. Reset when profile/session changes.
  const [accumulatedTransactions, setAccumulatedTransactions] = useState([]);
  useEffect(() => { setAccumulatedTransactions([]); }, [sessionId]);

  // Background analysis insights — polled once after profile loads (~2.5s for agents to finish).
  // Retries once if not ready. Dismissed state resets on profile change.
  const [profileInsights, setProfileInsights] = useState(null);
  const [insightsDismissed, setInsightsDismissed] = useState(false);
  const [insightsBannerOpen, setInsightsBannerOpen] = useState(false);
  const [insightsRunning, setInsightsRunning] = useState(false);
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState(ALL_INSIGHT_AGENTS);
  const [fraudReports, setFraudReports] = useState(() => {
    try { return JSON.parse(localStorage.getItem('iva_fraud_reports') || '{}'); } catch { return {}; }
  });
  const insightsPollCancelRef = useRef(null);

  // Starts a polling loop for insights; accepts initial delay in ms.
  const startInsightsPoll = (sid, initialDelay = 2500) => {
    if (insightsPollCancelRef.current) insightsPollCancelRef.current();
    let cancelled = false;
    insightsPollCancelRef.current = () => { cancelled = true; };
    const poll = async () => {
      await new Promise(r => setTimeout(r, initialDelay));
      if (cancelled) return;
      const result = await fetchInsights(sid);
      if (cancelled) return;
      if (result.ready) {
        setProfileInsights(result);
        setInsightsRunning(false);
      } else {
        await new Promise(r => setTimeout(r, 3000));
        if (cancelled) return;
        const retry = await fetchInsights(sid);
        if (!cancelled) {
          if (retry.ready) setProfileInsights(retry);
          setInsightsRunning(false);
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  };

  useEffect(() => {
    setProfileInsights(null); setInsightsDismissed(false); setInsightsBannerOpen(false);
    setInsightsRunning(false); setAgentPickerOpen(false); setSelectedAgents(ALL_INSIGHT_AGENTS);
    if (!sessionId) return;
    startInsightsPoll(sessionId, 2500);
    return () => { if (insightsPollCancelRef.current) insightsPollCancelRef.current(); };
  }, [sessionId]);

  const [history, setHistory] = useState(() => {
    if (sessionToLoad) return sessionToLoad.history;
    return activeHistory || initialChatHistory;
  });

  // ─── In-chat Swipe (advisor matching) flow ─────────────────────────────────
  // null when no flow is active. See screens/swipe/chatSwipeFlow.js for the
  // scripted step engine this drives — everything renders as chat bubbles +
  // inline cards (components/SwipeCards.js), never a navigation or overlay.
  const [swipeState, setSwipeState] = useState(null);
  const [swipePrefs, setSwipePrefs] = useState(null);

  useEffect(() => {
    const customerId = sessionSummary?.customerId;
    if (!customerId) return;
    getSwipePreferences(customerId).then(prefs => { if (prefs) setSwipePrefs(prefs); }).catch(() => {});
  }, [sessionSummary?.customerId]);

  const beginSwipe = (seedIntent, advisorTypeFilter) => {
    const { nextState, botMessage, card } = startSwipeFlow(seedIntent, swipePrefs, { advisorTypeFilter });
    setSwipeState(nextState);
    setHistory(prev => [...prev, { id: generateId(), text: botMessage, isBot: true, timestamp: nowLabel(), swipeCard: card }]);
  };

  // answer: the raw value the step engine expects. label: optional human-readable
  // text for a synthetic user bubble — passed by card taps (a click isn't its own
  // chat message) and omitted when the real typed message already is one (the
  // what-brings-you free-text path, routed here from handleSend below).
  const advanceSwipe = (answer, label) => {
    setSwipeState(current => {
      if (!current) return current;
      const { nextState, botMessage, card, done, skipBotMessage } = advanceSwipeFlow(current, answer);
      if (!skipBotMessage) {
        setHistory(prev => {
          const next = label ? [...prev, { id: generateId(), text: label, isUser: true, timestamp: nowLabel() }] : prev;
          return [...next, { id: generateId(), text: botMessage, isBot: true, timestamp: nowLabel(), swipeCard: card }];
        });
      }
      return done ? null : nextState;
    });
  };

  // ─── In-chat EMT (self-serve Interac e-Transfer) flow ─────────────────────
  const [emtState, setEmtState] = useState(null);

  const beginEMT = () => {
    const { nextState, botMessage, card } = startEMTFlow();
    setEmtState(nextState);
    setHistory(prev => [...prev, { id: generateId(), text: botMessage, isBot: true, timestamp: nowLabel(), emtCard: card }]);
  };

  const advanceEMT = (answer, label) => {
    setEmtState(current => {
      if (!current) return current;
      const { nextState, botMessage, card, done } = advanceEMTFlow(current, answer);
      setHistory(prev => {
        const next = label ? [...prev, { id: generateId(), text: label, isUser: true, timestamp: nowLabel() }] : prev;
        return [...next, { id: generateId(), text: botMessage, isBot: true, timestamp: nowLabel(), emtCard: card }];
      });
      return done ? null : nextState;
    });
  };

  // ─── Agent-facilitated EMT (collect details, queue for agent review) ─────────
  const [agentEmtState, setAgentEmtState] = useState(null);

  const beginAgentEMT = () => {
    const { nextState, botMessage, card } = startAgentEMTFlow();
    setAgentEmtState(nextState);
    setHistory(prev => [...prev, { id: generateId(), text: botMessage, isBot: true, timestamp: nowLabel(), agentEmtCard: card }]);
  };

  const advanceAgentEMT = (answer, label) => {
    setAgentEmtState(current => {
      if (!current) return current;
      const { nextState, botMessage, card, done } = advanceAgentEMTFlow(current, answer);
      setHistory(prev => {
        const next = label ? [...prev, { id: generateId(), text: label, isUser: true, timestamp: nowLabel() }] : prev;
        return [...next, { id: generateId(), text: botMessage, isBot: true, timestamp: nowLabel(), agentEmtCard: card }];
      });
      return done ? null : nextState;
    });
  };

  const [inputText, setInputText] = useState('');
  const [showCarousel, setShowCarousel] = useState(true);
  const [modelName, setModelName] = useState('command-r-08-2024');
  const [chatTitle, setChatTitle] = useState(() => sessionToLoad?.title || null);
  const [currentSuggestions, setCurrentSuggestions] = useState(chatSuggestions);
  const [saveStatus, setSaveStatus] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const abortControllerRef = useRef(null);
  const chatEndRef = useRef(null);
  const suggestionsRef = useRef(null);
  const chatScrollRef = useRef(null);

  // ─── Voice / TTS pipeline (Option A merger) ───────────────────────────────
  const {
    speak, cancelSpeech, isSpeaking, currentAudioRef,
  } = useTTS({
    selectedBackend, voiceOut, ttsEngine, setTtsEngine,
    elVoiceId, setElVoiceId, openaiVoiceId, setOpenaiVoiceId, qwenVoiceId, setQwenVoiceId, log,
  });

  // We send through the existing handleSend pipeline so retries / pinning /
  // session-save / summarization all keep working unchanged.
  const handleSendRef = useRef(null);

  const { isListening, transcript, setTranscript, supported: voiceSupported, toggleListening } = useVoiceInput({
    isProcessing,
    isSpeaking,
    cancelSpeech,
    onFinalTranscript: (text) => {
      if (text && text.trim()) {
        log('user', `Mic: "${text.trim().slice(0, 80)}"`);
        handleSendRef.current?.(text.trim());
      }
    },
    log,
  });
  const cumulativeUserMsgCountRef = useRef(0);
  const titleLastUpdatedAtRef = useRef(sessionToLoad?.title ? Infinity : 0);
  const isSummarizingRef = useRef(false);

  // Reflect the currently-selected model in the chat header — updates both when a
  // model is picked from the dropdown and when the provider switch changes it.
  // (After a send, the server-confirmed model below overrides this if it differs.)
  useEffect(() => {
    if (selectedModel) setModelName(selectedModel);
  }, [selectedModel]);

  useEffect(() => {
    const el = suggestionsRef.current;
    if (!el) return;
    const onWheel = (e) => { if (e.deltaY !== 0) { e.preventDefault(); el.scrollLeft += e.deltaY; } };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [history]);

  useEffect(() => { if (onHistoryChange) onHistoryChange(history); }, [history]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cumCount = cumulativeUserMsgCountRef.current;
    const lastAt   = titleLastUpdatedAtRef.current;
    const atMilestone = TITLE_MILESTONES.some(m => cumCount >= m && lastAt < m);
    const afterMilestones = cumCount > TITLE_MILESTONES[TITLE_MILESTONES.length - 1];
    const atInterval = afterMilestones &&
      Math.floor((cumCount - TITLE_MILESTONES[TITLE_MILESTONES.length - 1]) / TITLE_INTERVAL) >
      Math.floor((Math.max(lastAt, TITLE_MILESTONES[TITLE_MILESTONES.length - 1]) - TITLE_MILESTONES[TITLE_MILESTONES.length - 1]) / TITLE_INTERVAL);
    if (!atMilestone && !atInterval) return;
    // Skip backend title generation for local providers — they can't handle concurrent
    // requests and titleFromMessage already set a cheap title from the first message.
    if (selectedBackend === 'ollama' || selectedBackend === 'omlx') return;
    titleLastUpdatedAtRef.current = cumCount;
    const sample = history.slice(-10).map(m => `${m.isUser ? 'User' : 'Assistant'}: ${m.text.slice(0, 200)}`).join('\n');
    callBackendAPI(
      `Based on this banking conversation, generate a concise 4-6 word title capturing the main topic. Reply with ONLY the title, no quotes, no trailing punctuation:\n\n${sample}`,
      [], selectedBackend, selectedModel
    ).then(({ text }) => { const title = text.trim().replace(/^["']|["']$/g, '').slice(0, 60); if (title) setChatTitle(title); }).catch(() => {});
  }, [history]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (history.length < SUMMARY_THRESHOLD || isSummarizingRef.current) return;
    isSummarizingRef.current = true;
    const toSummarize = history.slice(0, history.length - SUMMARY_KEEP_RECENT);
    const recent = history.slice(history.length - SUMMARY_KEEP_RECENT);
    const conversationText = toSummarize.map(m => `${m.isUser ? 'User' : 'Assistant'}: ${m.text}`).join('\n\n');
    callBackendAPI(
      `Summarize this banking conversation in 3-5 concise bullet points, keeping all key facts, figures, and decisions:\n\n${conversationText}`,
      [], selectedBackend, selectedModel
    ).then(({ text }) => {
      const summaryMsg = { id: generateId(), text: text.trim(), isBot: true, isSummary: true, timestamp: nowLabel() };
      setHistory([summaryMsg, ...recent]);
    }).catch(() => { isSummarizingRef.current = false; });
  }, [history.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProfileUploadClick = async () => {
    if (!loadedProfileData) {
      setUploadStatus('No profile selected');
      setTimeout(() => setUploadStatus(''), 2500);
      return;
    }

    const profilePayload = loadedProfileData;
    setProfileUploadData(profilePayload);
    setUploadStatus(`Sending profile: ${loadedProfileData.name}`);

    try {
      const ack = await callBackendAPI(
        'Profile data is uploaded and should be used for context.',
        history,
        selectedBackend,
        selectedModel,
        profilePayload,
        null,
        ollamaNoThink,
        composedSystemPrompt,
      );
      setHistory((prev) => [...prev, { id: `profile-upload-${Date.now()}`, text: `Profile sent to LLM — ${ack.text}`, isBot: true, timestamp: nowLabel() }]);
      setUploadStatus('Profile sent');
    } catch (error) {
      setUploadStatus(`Send failed: ${error.message}`);
      console.error('Profile upload backend error:', error);
    } finally {
      setTimeout(() => setUploadStatus(''), 3000);
    }
  };

  const handleStop = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsProcessing(false);
  };

  const handleSend = async (text) => {
    if (!text.trim() || isProcessing) return;
    console.log('[ChatUI] Sending message:', text.slice(0, 100), '...');
    console.log('[ChatUI] Selected backend:', selectedBackend, 'Model:', selectedModel);
    console.log('[ChatUI] composedSystemPrompt:', composedSystemPrompt ? composedSystemPrompt.slice(0, 120) + '…' : '(null — server default)');
    cumulativeUserMsgCountRef.current++;
    const userMsg = { id: generateId(), text: text, isUser: true, timestamp: nowLabel() };
    setHistory(prev => [...prev, userMsg]);
    setInputText('');

    // Swipe flow is waiting for the user to describe their need. Advance the
    // step engine (moves to llm-pending, skipBotMessage so no duplicate bubble),
    // then fall through so the LLM answers the question naturally.
    if (swipeState && stepAcceptsFreeText(swipeState.step)) {
      advanceSwipe(text);
    }

    // Fraud/scam guard (plan 270): if the customer pastes a message that matches
    // classic scam patterns (gift-card demand, urgent transfer, "bank security"
    // impersonation), warn them BEFORE anything else with non-accusatory guidance.
    // Only worth checking longer pastes; the classifier itself needs 2+ signals.
    if (text.trim().length > 40) {
      try {
        const scam = await checkScamMessage(text);
        if (scam?.flagged && scam.guidance) {
          const reasons = (scam.signals || []).map(s => s.label).join('; ');
          setHistory(prev => [...prev, {
            id: `scam-${Date.now()}`,
            text: `⚠️ Heads up — this looks like a scam${reasons ? ` (${reasons})` : ''}.\n\n${scam.guidance}`,
            isBot: true,
            timestamp: nowLabel(),
          }]);
        }
      } catch (_) { /* non-blocking — never let the guard stop a normal message */ }
    }
    // Local cheap title from the first user message — no backend round-trip.
    // The backend-driven refinement at TITLE_MILESTONES still runs later if
    // the model produces a stronger summary.
    if (!chatTitle) {
      const t = titleFromMessage(text);
      if (t) setChatTitle(t);
    }
    setInputText('');
    setShowCarousel(false);
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsProcessing(true);
    try {
      // Use session cache path when available (no raw JSON per turn);
      // fall back to inline profileData if session hasn't loaded yet.
      const baseProfileData = profileUploadData || chatProfilePayload;
      const activeProfileData = sessionId ? null : baseProfileData;

      // ── Step 1: Run filter agent, accumulate results ───────────────────────
      // Filter agent fetches a targeted slice of transactions for this question.
      // Results are merged into accumulatedTransactions so follow-up questions
      // have access to data from prior turns — not just the current turn's slice.
      let filteredStats = null;
      let mergedTxns = accumulatedTransactions; // updated synchronously if filter returns results
      const isAnsweringClarification = !!pendingClarification;

      if (sessionId && sessionSummary) {
        const filterQuestion = isAnsweringClarification
          ? pendingClarification.originalQuestion
          : text;

        if (isAnsweringClarification || needsDetailedContext(text)) {
          try {
            const filterResult = await filterProfile(sessionId, filterQuestion, history);

            if (filterResult.clarifyNeeded && !isAnsweringClarification) {
              // Ask user for time range — don't call main LLM yet
              const clarifyMsg = {
                id: `clarify-${Date.now()}`,
                text: filterResult.clarifyPrompt,
                isBot: true,
                timestamp: nowLabel(),
              };
              setHistory(prev => [...prev, clarifyMsg]);
              setPendingClarification({ originalQuestion: text });
              setIsProcessing(false);
              log('system', 'Filter agent: requesting time range clarification');
              return;
            }

            const newTxns = filterResult.filteredTransactions || [];
            filteredStats = filterResult.stats || null;
            if (isAnsweringClarification) setPendingClarification(null);

            if (newTxns.length) {
              // Compute merged list synchronously so activeTxns (below) includes
              // this turn's results — setAccumulatedTransactions is async.
              const existingKeys = new Set(accumulatedTransactions.map(txnKey));
              const deduped = newTxns.filter(t => !existingKeys.has(txnKey(t)));
              mergedTxns = [...accumulatedTransactions, ...deduped];
              setAccumulatedTransactions(mergedTxns);
              log('system', `Filter: +${deduped.length} new txns (${mergedTxns.length} total accumulated) | ${filteredStats?.period || ''}`);
            }
          } catch (_) {
            if (isAnsweringClarification) setPendingClarification(null);
          }
        }
      }

      // ── Step 2: Token budget check + compaction ────────────────────────────
      // Estimate total tokens about to be sent. If > 80% of the model's context
      // window, compact the older portion of conversation history before calling
      // the LLM. accumulatedTransactions are pruned (oldest first) if they alone
      // exceed 60% of the budget.
      const contextLimit = MODEL_CONTEXT_WINDOWS[selectedModel] ?? DEFAULT_CONTEXT_WINDOW;
      const budget80 = Math.floor(contextLimit * 0.8);
      const budget60 = Math.floor(contextLimit * 0.6);

      // mergedTxns was updated synchronously in the filter block above (if filter ran).
      // This ensures the current turn's new transactions are included even though
      // setAccumulatedTransactions is async and won't reflect until next render.
      let activeTxns = mergedTxns;

      // Prune accumulated transactions if they alone eat > 60% of budget
      const txnTokens = estimateTokens(JSON.stringify(activeTxns));
      if (txnTokens > budget60 && activeTxns.length > 10) {
        // Keep most recent transactions (sorted by date desc already from filter)
        const maxTxns = Math.floor(activeTxns.length * (budget60 / txnTokens));
        activeTxns = activeTxns.slice(-Math.max(maxTxns, 10));
        log('system', `Context: pruned accumulated txns to ${activeTxns.length} (token budget)`);
      }

      // Estimate full context size
      const sysTokens      = estimateTokens(composedSystemPrompt);
      const summaryTokens  = estimateTokens(JSON.stringify(sessionSummary));
      const historyTokens  = history.reduce((s, m) => s + estimateTokens(m.text), 0);
      const totalEstimated = sysTokens + summaryTokens + txnTokens + historyTokens;

      // Working copy of history — may be replaced by compaction
      let activeHistory = history;

      if (totalEstimated > budget80 && history.length > KEEP_LAST_TURNS) {
        const olderTurns = history.slice(0, -KEEP_LAST_TURNS);
        const recentTurns = history.slice(-KEEP_LAST_TURNS);
        log('system', `Context: ~${totalEstimated} tok > ${budget80} (80% of ${contextLimit}) — compacting ${olderTurns.length} turns [${COMPACTION_STRATEGY}]`);

        let summaryText = '';
        if (COMPACTION_STRATEGY === 'llm') {
          try {
            const { summary } = await compactHistory(olderTurns, selectedBackend, selectedModel);
            summaryText = summary;
          } catch (_) {
            // LLM compact failed — fall through to truncate
            log('system', 'Context: LLM compact failed, falling back to truncate');
          }
        }

        if (!summaryText) {
          summaryText = `[${olderTurns.length} earlier messages truncated to fit context window]`;
        }

        const compactedMsg = {
          id: `compacted-${Date.now()}`,
          isBot: true,
          isCompacted: true,
          text: `_[Earlier conversation summary: ${summaryText}]_`,
          timestamp: '',
        };
        activeHistory = [compactedMsg, ...recentTurns];
        setHistory(activeHistory);
        log('system', `Context: history compacted → ${activeHistory.length} turns`);
      }

      const effectiveSystemPrompt = simDate
        ? `[SIMULATION MODE — treat "${simDate}" as today's date for all advice, balances, and context.]\n\n${composedSystemPrompt ?? ''}`
        : composedSystemPrompt;
      log('system', `Prompt: ${effectiveSystemPrompt ? effectiveSystemPrompt.length + ' chars (custom)' : 'server default'}${sessionId ? ' | session active' : ''}${simDate ? ` | simDate=${simDate}` : ''}`);
      const { text: botResponseText, model } = await callBackendAPI(text, activeHistory, selectedBackend, selectedModel, activeProfileData, controller.signal, ollamaNoThink, effectiveSystemPrompt, currentUser, sessionId, sessionSummary, activeTxns, filteredStats);
      console.log('[ChatUI] Received response:', botResponseText.slice(0, 100), '... from model:', model);
      if (model) { setModelName(model); log('api', `Model: ${model}`); }
      // If the customer's message expressed a goal ("save $3,000 for a trip"),
      // offer a confirm-gated "Create this goal" card under the reply. Only when a
      // profile session is active (the action needs a server-side session).
      const goalAction = sessionId ? parseGoalIntent(text) : null;
      const botMsg = { id: generateId(), text: botResponseText, isBot: true, timestamp: nowLabel(), goalAction };
      setHistory(prev => [...prev, botMsg]);
      log('bot', `Response (${botResponseText.length} chars)`);
      // If the swipe flow is parked at llm-pending waiting for the LLM response,
      // advance now that the response is in — this appends the advisor-offer card.
      setSwipeState(current => {
        if (current?.step !== 'llm-pending') return current;
        const { nextState, botMessage, card } = advanceSwipeFlow(current, null);
        setHistory(prev => [...prev, { id: generateId(), text: botMessage, isBot: true, timestamp: nowLabel(), swipeCard: card }]);
        return nextState;
      });
      // Memory write path — fire-and-forget: server's extraction agent learns
      // durable facts (likes, goals, concept corrections, style) from the exchange.
      if (sessionId) {
        extractMemory(sessionId, text, botResponseText, sessionSummary?.customerId).then(r => {
          if (r?.changes?.length) log('system', `Memory: ${r.changes.join('; ')}`);
        });
      }
      // Proactive confirmation — if the customer's message touches a guess IVA made
      // about them, attach the matching prompts to this reply so a tap-to-confirm
      // card renders under it. Quiet when nothing is relevant.
      if (sessionSummary?.customerId) {
        getRelevantAssumptions(sessionSummary.customerId, text).then(prompts => {
          if (prompts.length) {
            setHistory(prev => prev.map(m => m.id === botMsg.id ? { ...m, assumptionPrompts: prompts } : m));
            log('system', `Assumptions to confirm: ${prompts.map(p => p.id).join(', ')}`);
          }
        });
      }
      // Voice-mode auto-speak — summarize first so TTS reads a short TLDR
      // rather than the full reply. Falls back to cleaned text if summarize fails.
      if (voiceOut && botResponseText) {
        const cleanText = botResponseText.replace(/```[\s\S]*?```/g, '').replace(/[*_`#>]/g, '').trim();
        fetch('/api/summarize', {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ text: cleanText, provider: selectedBackend, model: selectedModel, summarizePrompt, summarizeProvider }),
        })
          .then(r => r.ok ? r.json() : null)
          .then(data => speak(data?.tldr || cleanText.slice(0, 1200)))
          .catch(() => speak(cleanText.slice(0, 1200)));
      }
      const extracted = extractFollowUpQuestions(botResponseText);
      if (extracted.length >= 2) { setCurrentSuggestions(extracted); }
      else { setCurrentSuggestions(prev => [...prev.slice(1), prev[0]]); }
    } catch (error) {
      if (error.name !== 'AbortError' && error.message !== 'AbortError') {
        const errorMsg = { id: generateId(), text: "Sorry, I encountered an error. Please try again.", isBot: true, failed: true, retryText: text, timestamp: nowLabel() };
        setHistory(prev => [...prev, errorMsg]);
      }
    } finally {
      abortControllerRef.current = null;
      setIsProcessing(false);
    }
  };
  // Expose latest handleSend to the voice-input transcript callback (decoupled
  // so the hook doesn't have to re-bind the closure every render).
  useEffect(() => { handleSendRef.current = handleSend; });

  // Consume chatSeed once: when a hand-off screen sets it, we auto-send as
  // the user's first message. The ref guard prevents re-firing on rerender;
  // onSeedConsumed clears it in App so the seed isn't stale next visit.
  const seedConsumedRef = useRef(false);
  useEffect(() => {
    if (!chatSeed || seedConsumedRef.current) return;
    seedConsumedRef.current = true;
    if (chatSeed === SWIPE_SEED) beginSwipe();
    else handleSendRef.current?.(chatSeed);
    onSeedConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSeed]);

  const handleNewChat = () => {
    const userMessages = history.filter(m => m.isUser);
    if (userMessages.length > 0 && onSaveSession) {
      const preview = userMessages[0].text.slice(0, 70);
      const timestamp = new Date().toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
      onSaveSession({ id: generateId(), history: [...history], title: chatTitle || null, preview, timestamp, messageCount: userMessages.length });
    }
    setHistory(initialChatHistory);
    setInputText('');
    setShowCarousel(true);
    setCurrentSuggestions(chatSuggestions);
  };

  // Fire-and-forget feedback POST. Records counts only — message text never
  // leaves the client. Server-side rate-limit / log shape lives in
  // server.js → POST /api/feedback.
  const handleFeedback = (type) => {
    log('user', `Feedback: ${type}`);
    fetch('/api/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type,
        user: loadedProfileData?.name || loadedProfileData?.profile?.customer?.email || 'anonymous',
      }),
    }).catch(() => { /* network failures are silent — UI never blocks on feedback */ });
  };

  // Customer tapped Yes/No on a derived guess. Optimistically mark the row resolved
  // (so the buttons collapse to a confirmation), then persist the decision.
  const handleAssumptionDecision = async (msgId, prompt, decision) => {
    const customerId = sessionSummary?.customerId;
    if (!customerId) return;
    setHistory(prev => prev.map(m => {
      if (m.id !== msgId) return m;
      const prompts = (m.assumptionPrompts || []).map(p => p.id === prompt.id ? { ...p, resolved: decision } : p);
      return { ...m, assumptionPrompts: prompts };
    }));
    log('user', `Assumption ${decision}: ${prompt.id}`);
    const r = await decideAssumption(customerId, prompt.id, decision);
    if (decision === 'confirm' && r?.fact) log('system', `Confirmed fact: ${r.fact}`);
  };

  // A goal was created from chat — push the fresh goals array into the loaded
  // profile so the Goals screen reflects it without a full profile reload.
  const handleGoalCreated = (goals) => {
    if (!Array.isArray(goals)) return;
    setLoadedProfileData(prev => (prev ? { ...prev, goals } : prev));
    log('system', `Goal created (${goals.length} total)`);
  };

  const handleRetry = async (retryText, failedMsgId) => {
    setHistory(prev => prev.map(m => m.id === failedMsgId ? { ...m, failed: false, text: 'Retrying…' } : m));
    try {
      const historyWithoutFailed = history.filter(m => m.id !== failedMsgId);
      const activeProfileData = sessionId ? null : (profileUploadData || chatProfilePayload);
      const retrySystemPrompt = simDate
        ? `[SIMULATION MODE — treat "${simDate}" as today's date for all advice, balances, and context.]\n\n${composedSystemPrompt ?? ''}`
        : composedSystemPrompt;
      const { text: botResponseText, model } = await callBackendAPI(retryText, historyWithoutFailed, selectedBackend, selectedModel, activeProfileData, null, ollamaNoThink, retrySystemPrompt, currentUser, sessionId, sessionSummary, []);
      if (model) setModelName(model);
      setHistory(prev => prev.map(m => m.id === failedMsgId ? { ...m, text: botResponseText, failed: false, retryText: undefined } : m));
    } catch (error) {
      setHistory(prev => prev.map(m => m.id === failedMsgId ? { ...m, text: 'Sorry, I encountered an error. Please try again.', failed: true } : m));
    }
  };

  const handleSaveSession = async () => {
    if (saveStatus === 'saving') return;
    setSaveStatus('saving');

    console.log('🔴 [SAVE] Starting save session...');

    const now = new Date();
    const date = [now.getFullYear(), String(now.getMonth()+1).padStart(2,'0'), String(now.getDate()).padStart(2,'0')].join('-');
    const time = [String(now.getHours()).padStart(2,'0'), String(now.getMinutes()).padStart(2,'0'), String(now.getSeconds()).padStart(2,'0')].join('-');
    const profileSlug = (loadedProfileData?.source || 'default').replace(/[^a-z0-9]/gi, '-').toLowerCase();
    const firstUserMsg = history.find(m => m.isUser);
    const titleSlug = chatTitle
      ? chatTitle.replace(/[^a-zA-Z0-9 ]/g, '').trim().replace(/\s+/g, '-').toLowerCase().slice(0, 40)
      : firstUserMsg ? firstUserMsg.text.slice(0, 30).replace(/[^a-zA-Z0-9 ]/g, '').trim().replace(/\s+/g, '-').toLowerCase() : 'session';
    const base = `${date}_${time}_${profileSlug}_${titleSlug}`;

    const triggerDownload = (href, filename) => {
      console.log(`📥 [SAVE] Downloading: ${filename}`);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(href);
    };

    try {
      // Build chat log
      console.log('📝 [SAVE] Building chat log...');
      const userMsgs = history.filter(m => m.isUser).length;
      const botMsgs  = history.filter(m => !m.isUser).length;
      const divider  = '─'.repeat(50);
      const logLines = [
        '╔══════════════════════════════════════════════════╗',
        '║            ABC Bank IVA — Chat Session Log       ║',
        '╚══════════════════════════════════════════════════╝',
        '', `  Date     : ${now.toLocaleString()}`, `  Profile  : ${loadedProfileData?.name || 'Default'}`,
        `  Model    : ${modelName || 'Unknown'}`, `  Messages : ${userMsgs} from you, ${botMsgs} from assistant`, '', divider, '',
        ...history.flatMap(msg => { const role = msg.isUser ? 'YOU' : 'ASSISTANT'; const ts = msg.id ? new Date(msg.id).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''; return [`[${role}${ts ? '  ·  ' + ts : ''}]`, msg.text || '(empty)', '']; }),
        divider, `End of session — ${history.length} total messages`,
      ];
      const logBlob = new Blob([logLines.join('\n')], { type: 'text/plain' });
      triggerDownload(URL.createObjectURL(logBlob), `${base}.log`);

      // Screenshot: capture the full scrollable chat container via html2canvas
      // so messages above/below the viewport are all included.
      console.log('📸 [SAVE] Starting screenshot...');

      try {
        if (!chatScrollRef.current) throw new Error('chat scroll container not mounted');
        const canvas = await captureChatScroll(chatScrollRef.current, {
          title: chatTitle,
          model: modelName,
        });
        console.log('✅ [SAVE] Canvas generated:', canvas.width, '×', canvas.height);
        downloadCanvas(canvas, `${base}.png`);
        console.log('✅ [SAVE] Screenshot complete!');
        setSaveStatus('saved');
      } catch (screenshotError) {
        console.error('❌ [SAVE] Screenshot error:', screenshotError);
        setSaveStatus('error');
      }

    } catch (e) {
      console.error('❌ [SAVE] Error:', e);
      setSaveStatus('error');
    }

    setTimeout(() => setSaveStatus(null), 2500);
  };

  const getFirstNMonthsTransactions = (n = 4) => {
    const allItems = [];
    transactionsData.forEach(group => group.items.forEach(i => allItems.push(i)));
    Object.values(accountTransactions || {}).forEach(accGroups => accGroups.forEach(g => g.items.forEach(i => allItems.push(i))));
    const itemsWithDate = allItems.filter(it => it.datetime).map(it => ({ ...it, _date: new Date(it.datetime) })).sort((a, b) => b._date - a._date);
    const monthKeys = [];
    for (const it of itemsWithDate) {
      const key = `${it._date.getFullYear()}-${it._date.getMonth() + 1}`;
      if (!monthKeys.includes(key)) monthKeys.push(key);
      if (monthKeys.length >= n) break;
    }
    return itemsWithDate.filter(it => monthKeys.includes(`${it._date.getFullYear()}-${it._date.getMonth() + 1}`)).map(({ _date, ...rest }) => rest);
  };

  const handleSendTransactions = async () => {
    const txs = getFirstNMonthsTransactions(4);
    if (!txs || txs.length === 0) { setHistory(prev => [...prev, { id: generateId(), text: 'No transactions available to send.', isBot: true, timestamp: nowLabel() }]); return; }
    const userMsg = { id: generateId(), text: 'Uploading recent transactions for analysis.', isUser: true, timestamp: nowLabel() };
    setHistory(prev => [...prev, userMsg]);
    setShowCarousel(false);
    try {
      const { text: botResponseText } = await callBackendAPI(`TRANSACTION_UPLOAD:${JSON.stringify(txs)}`, [...history, userMsg], selectedBackend, selectedModel, chatProfilePayload);
      setHistory(prev => [...prev, { id: generateId(), text: botResponseText, isBot: true, timestamp: nowLabel() }]);
    } catch (err) {
      setHistory(prev => [...prev, { id: generateId(), text: 'Failed to send transactions.', isBot: true, timestamp: nowLabel() }]);
    }
  };

  // Long-press in the empty (carousel) state opens the full-screen
  // conversation surface — accelerator for power users who don't want to
  // hunt for the entry icon in the composer.
  const longPressTimerRef = useRef(null);
  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  const startLongPress = () => {
    if (!showCarousel) return;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      log('system', 'Conversation mode opened (long-press)');
      setConversationMode(true);
    }, 700);
  };

  // Long-press on the mic button → conversation mode. Short tap = regular
  // speech-to-text (existing behavior). The fired ref tells the click
  // handler to skip its toggleListening so the gesture isn't double-counted.
  const micLongPressTimerRef  = useRef(null);
  const micVisualTimerRef     = useRef(null); // separate timer for ring animation
  const micLongPressFiredRef  = useRef(false);
  const [micLongPressActive, setMicLongPressActive] = useState(false);
  // Immediate visual feedback — turns the button red the moment the user taps,
  // before SpeechRecognition confirms it opened (which can take one React cycle).
  const [pendingListen, setPendingListen] = useState(false);

  // Live refs so the 600 ms timer callback never reads stale closure values.
  const isSpeakingRef      = useRef(isSpeaking);
  const isListeningRef     = useRef(isListening);
  const toggleListeningRef = useRef(toggleListening);
  const cancelSpeechRef    = useRef(cancelSpeech);
  useEffect(() => { isSpeakingRef.current      = isSpeaking;      }, [isSpeaking]);
  useEffect(() => { isListeningRef.current     = isListening;     }, [isListening]);
  useEffect(() => { toggleListeningRef.current = toggleListening; }, [toggleListening]);
  useEffect(() => { cancelSpeechRef.current    = cancelSpeech;    }, [cancelSpeech]);

  const cancelMicLongPress = () => {
    if (micLongPressTimerRef.current) {
      clearTimeout(micLongPressTimerRef.current);
      micLongPressTimerRef.current = null;
    }
    if (micVisualTimerRef.current) {
      clearTimeout(micVisualTimerRef.current);
      micVisualTimerRef.current = null;
    }
    setMicLongPressActive(false);
  };
  const startMicLongPress = () => {
    cancelMicLongPress();
    micLongPressFiredRef.current = false;

    // Delay visual ring by 600 ms — accidental short holds never see the ring.
    // Ring then runs 600ms → 3000ms = 2400ms, matching the CSS animation.
    micVisualTimerRef.current = setTimeout(() => {
      setMicLongPressActive(true);
    }, 600);

    // Open conversation mode at 3000 ms (3-second intentional hold).
    micLongPressTimerRef.current = setTimeout(() => {
      micLongPressFiredRef.current = true;
      setMicLongPressActive(false);
      log('system', 'Conversation mode opened (long-press mic)');
      if (isSpeakingRef.current)  cancelSpeechRef.current();
      if (isListeningRef.current) toggleListeningRef.current();
      setConversationMode(true);
    }, 3000);
  };

  // Full-screen voice surface — replaces the chat UI while open. Hooks /
  // pipeline are shared, so any voice turns landed by the user during
  // conversation mode are already in `history` when they exit.
  if (conversationMode) {
    return (
      <div className={`screen chat-screen${isModal ? ' chat-screen--modal' : ''}`}>
        <ConversationMode
          isListening={isListening}
          isSpeaking={isSpeaking}
          isProcessing={isProcessing}
          transcript={transcript}
          toggleListening={toggleListening}
          cancelSpeech={cancelSpeech}
          voiceSupported={voiceSupported}
          onExit={() => {
            log('system', 'Conversation mode closed');
            setConversationMode(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className={`screen chat-screen${isModal ? ' chat-screen--modal' : ''}`}>
      <div className="simple-header chat-header-outer">
        <div className="chat-header-main-row">
          {isModal ? (
            <MdChat style={{ fontSize: 24, color: 'white' }} />
          ) : (
            <>
              <MdChevronLeft className="back-icon" onClick={onBack || (() => onNavigate('home'))} />
              <MdChat style={{ fontSize: 24, color: 'white' }} />
            </>
          )}
          <button
            className="new-chat-button"
            onClick={handleProfileUploadClick}
            title="Send selected profile to backend"
            aria-label="Send selected profile"
            style={{ padding: '6px 8px', marginRight: '6px' }}
          >
            <MdUploadFile style={{ fontSize: 18, display: 'block' }} />
          </button>
          <div style={{ display: 'flex', gap: 2, alignItems: 'center', marginLeft: 'auto' }}>
            <button
              className={`new-chat-button${voiceOut ? ' active' : ''}`}
              onClick={() => {
                const next = !voiceOut;
                setVoiceOut(next);
                if (!next) { window.speechSynthesis?.cancel(); if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; } }
                log('tts', `Voice output ${next ? 'enabled' : 'muted'}`);
              }}
              title={voiceOut ? 'Mute voice replies' : 'Enable voice replies'}
              aria-label={voiceOut ? 'Mute voice replies' : 'Enable voice replies'}
              aria-pressed={voiceOut}
              style={{ padding: '6px 8px' }}
            >
              {voiceOut ? <MdVolumeUp style={{ fontSize: 18, display: 'block' }} /> : <MdVolumeOff style={{ fontSize: 18, display: 'block' }} />}
            </button>
            <button className="new-chat-button" onClick={handleNewChat} title="New chat" aria-label="New chat" style={{ padding: '6px 8px' }}>
              <MdAddComment style={{ fontSize: 18, display: 'block' }} />
            </button>
            <button className="new-chat-button" onClick={() => onNavigate('chatHistory')} title="Chat history" aria-label="Chat history" style={{ padding: '6px 8px' }}>
              <MdHistory style={{ fontSize: 18, display: 'block' }} />
            </button>
            <button
              className="new-chat-button save-chat-btn"
              onClick={handleSaveSession}
              title="Save chat log + screenshot"
              disabled={saveStatus === 'saving'}
              aria-label="Save chat log"
              style={{
                width: 32, height: 32, borderRadius: '50%', padding: 0, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: saveStatus === 'saved'  ? 'rgba(0,220,100,0.35)'
                             : saveStatus === 'error'  ? 'rgba(255,80,80,0.35)'
                             : saveStatus === 'saving' ? 'rgba(255,255,255,0.1)'
                             : 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.25)',
                cursor: saveStatus === 'saving' ? 'wait' : 'pointer',
                transition: 'background-color 0.3s',
              }}
            >
              <MdCameraAlt style={{ fontSize: 16, color: '#fff', opacity: saveStatus === 'saving' ? 0.4 : 1 }} />
            </button>
          </div>
        </div>
        <div className="chat-model-label">
          {chatTitle && <span className="chat-title-text">{chatTitle}</span>}
          {chatTitle && <span className="chat-title-sep"> · </span>}
          <span>{modelName}</span>
        </div>
        {uploadStatus && (
          <div style={{ fontSize: 12, color: '#e2f1ff', textAlign: 'center', marginTop: 6 }}>
            {uploadStatus}
          </div>
        )}
      </div>

      {/* IVA noticed… — sticky collapsible banner, stays visible throughout the chat */}
      {(insightsRunning || (profileInsights?.ready && !insightsDismissed && (profileInsights.narrative?.length > 0 || profileInsights.subscriptions?.length > 0 || profileInsights.anomalies?.length > 0 || profileInsights.spendDigest?.totalSpend > 0 || profileInsights.healthScore?.indicators?.length > 0))) && (() => {
        const label = insightsRunning
          ? 'Analyzing…'
          : profileInsights.narrative?.length > 0
            ? `IVA noticed ${profileInsights.narrative.length} thing${profileInsights.narrative.length !== 1 ? 's' : ''}`
            : profileInsights.subscriptions?.length > 0
              ? `${profileInsights.subscriptions.length} recurring charge${profileInsights.subscriptions.length !== 1 ? 's' : ''} · ~$${Math.round(profileInsights.subscriptions.reduce((s, r) => s + r.monthlyEst, 0))}/mo`
              : `${profileInsights.anomalies.length} unusual charge${profileInsights.anomalies.length !== 1 ? 's' : ''} detected`;

        const AGENT_META = [
          { key: 'anomalies',     label: 'Unusual Charges' },
          { key: 'subscriptions', label: 'Subscriptions' },
          { key: 'budgetPulse',   label: 'Spending Shifts' },
          { key: 'narrative',     label: 'AI Summary' },
          { key: 'spendDigest',   label: 'Spend Digest' },
          { key: 'healthScore',   label: 'Health Score' },
        ];

        const handleRunAgents = async (e) => {
          e.stopPropagation();
          if (!sessionId || insightsRunning) return;
          setAgentPickerOpen(false);
          setInsightsRunning(true);
          setProfileInsights(null);
          await runInsightsAgents(sessionId, selectedAgents);
          startInsightsPoll(sessionId, 500);
        };

        // In light mode the banner sits on a pale background, so the light-blue
        // label text washes out — use the app's dark green for legibility.
        const isLight = theme !== 'purple';
        const labelColor = isLight
          ? '#1b5e20'
          : (insightsRunning ? 'rgba(160,210,255,0.55)' : 'rgba(160,210,255,0.95)');
        // Header controls (↺ re-run, › expand, × dismiss) — match the text in light
        // mode; never leave a light glyph on the pale background.
        const iconColor   = isLight ? '#1b5e20'         : 'rgba(160,210,255,0.7)';
        const dismissColor = isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.35)';
        const iconActiveBg = isLight ? 'rgba(27,94,32,0.12)' : 'rgba(100,180,255,0.15)';
        const iconBorder = (active) => isLight
          ? (active ? 'rgba(27,94,32,0.45)' : 'rgba(27,94,32,0.22)')
          : (active ? 'rgba(100,180,255,0.4)' : 'rgba(100,180,255,0.2)');

        // The blue gradient + faint blue border vanish on a light page, leaving no
        // visible bar to pull down. In light mode use a green-tinted band with a
        // clear border so the collapsed header is obviously there and tappable.
        const bannerBg = isLight
          ? 'linear-gradient(135deg, rgba(27,94,32,0.12) 0%, rgba(27,94,32,0.06) 100%)'
          : 'linear-gradient(135deg, rgba(21,101,192,0.22) 0%, rgba(13,71,161,0.15) 100%)';
        const bannerBorder = isLight ? 'rgba(27,94,32,0.30)' : 'rgba(100,160,255,0.2)';

        return (
          <div style={{
            flexShrink: 0,
            background: bannerBg,
            borderBottom: '1px solid ' + bannerBorder,
            backdropFilter: 'blur(6px)',
            overflow: 'hidden',
            transition: 'max-height 0.3s ease',
            maxHeight: insightsBannerOpen ? '50vh' : 36,
          }}>
            {/* Header row — always visible */}
            <div
              onClick={() => { if (!insightsRunning) setInsightsBannerOpen(o => !o); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0 10px 0 14px', height: 36, cursor: insightsRunning ? 'default' : 'pointer', userSelect: 'none',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: labelColor, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                <span style={{ fontSize: 13, display: 'inline-block', animation: insightsRunning ? 'agent-spin 1.2s linear infinite' : 'none' }}>✨</span>
                {label}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {/* Re-run button */}
                {!insightsRunning && sessionId && (
                  <button
                    onClick={e => { e.stopPropagation(); setAgentPickerOpen(o => !o); setInsightsBannerOpen(true); }}
                    title="Re-run insights"
                    style={{
                      background: agentPickerOpen ? iconActiveBg : 'none',
                      border: '1px solid ' + iconBorder(agentPickerOpen),
                      borderRadius: 4, color: iconColor, fontSize: 13,
                      cursor: 'pointer', lineHeight: 1, padding: '2px 6px', display: 'flex', alignItems: 'center',
                    }}
                    aria-label="Re-run insights"
                  >↺</button>
                )}
                {!insightsRunning && (
                  <span style={{ fontSize: 14, color: iconColor, transition: 'transform 0.2s', display: 'inline-block', transform: insightsBannerOpen ? 'rotate(90deg)' : 'rotate(0deg)', padding: '0 4px' }}>›</span>
                )}
                {!insightsRunning && (
                  <button
                    onClick={e => { e.stopPropagation(); setInsightsDismissed(true); setAgentPickerOpen(false); }}
                    style={{ background: 'none', border: 'none', color: dismissColor, fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: '0 2px' }}
                    aria-label="Dismiss insights"
                  >×</button>
                )}
              </div>
            </div>

            {/* Agent picker — shown when ↺ is clicked */}
            {agentPickerOpen && !insightsRunning && (() => {
              const pkBg     = isLight ? 'rgba(240,244,248,0.95)' : 'rgba(10,30,70,0.3)';
              const pkBorder = isLight ? 'rgba(0,0,0,0.10)'       : 'rgba(100,160,255,0.15)';
              const pkLabel  = isLight ? '#666666'                 : 'rgba(160,210,255,0.45)';
              const pkOnText = isLight ? '#111111'                 : 'rgba(180,220,255,0.9)';
              const pkOffText= isLight ? '#555555'                 : 'rgba(160,210,255,0.4)';
              const pkBtnBg  = (en) => isLight
                ? (en ? 'rgba(37,99,235,0.10)' : 'rgba(37,99,235,0.04)')
                : (en ? 'rgba(60,130,250,0.25)' : 'rgba(60,130,250,0.08)');
              const pkBtnBdr = (en) => isLight
                ? (en ? 'rgba(37,99,235,0.45)' : 'rgba(37,99,235,0.15)')
                : (en ? 'rgba(100,170,255,0.45)' : 'rgba(100,170,255,0.15)');
              const pkBtnClr = (en) => isLight
                ? (en ? '#1d4ed8' : '#93a3c0')
                : (en ? 'rgba(160,210,255,0.95)' : 'rgba(160,210,255,0.3)');
              return (
                <div
                  onClick={e => e.stopPropagation()}
                  style={{
                    padding: '8px 14px 10px',
                    borderTop: `1px solid ${pkBorder}`,
                    background: pkBg,
                  }}
                >
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: pkLabel, marginBottom: 7 }}>
                    Choose agents to run
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 10px', marginBottom: 9 }}>
                    {AGENT_META.map(({ key, label: agentLabel }) => {
                      const on = selectedAgents.includes(key);
                      return (
                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none' }}>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => setSelectedAgents(prev =>
                              on ? prev.filter(a => a !== key) : [...prev, key]
                            )}
                            style={{ accentColor: '#60a5fa', width: 12, height: 12, cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: 11, color: on ? pkOnText : pkOffText }}>{agentLabel}</span>
                        </label>
                      );
                    })}
                  </div>
                  <button
                    onClick={handleRunAgents}
                    disabled={selectedAgents.length === 0}
                    style={{
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                      background: pkBtnBg(selectedAgents.length > 0),
                      border: '1px solid ' + pkBtnBdr(selectedAgents.length > 0),
                      borderRadius: 5, color: pkBtnClr(selectedAgents.length > 0),
                      padding: '4px 14px', cursor: selectedAgents.length > 0 ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
                    }}
                  >Run ↺</button>
                </div>
              );
            })()}

            {/* Expanded structured data — only when results are available */}
            {insightsBannerOpen && !insightsRunning && profileInsights?.ready && (() => {
              // Light mode sits on a pale background — swap the blue/light neutrals for
              // dark green labels + near-black body text so the content stays legible.
              const secStyle = { padding: '7px 14px 6px', borderTop: '1px solid ' + (isLight ? 'rgba(0,0,0,0.08)' : 'rgba(100,160,255,0.1)') };
              const secLabel = { fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: isLight ? '#1b5e20' : 'rgba(160,210,255,0.45)', marginBottom: 4 };
              const row = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12, color: isLight ? '#1a1a1a' : 'rgba(220,235,255,0.85)', padding: '2px 0', gap: 8 };
              const hoverBg = isLight ? 'rgba(27,94,32,0.10)' : 'rgba(100,160,255,0.1)';
              const amountClr = isLight ? '#2e7d32' : 'rgba(160,210,255,0.75)';
              const askBtn = (questions) => (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setInsightsDismissed(true);
                    // Pre-load the remaining questions as suggestion chips so they're
                    // visible after IVA responds (overridden if IVA's reply contains its own questions)
                    if (questions.length > 1) setCurrentSuggestions(questions.slice(1));
                    handleSend(questions[0]);
                  }}
                  style={{ marginTop: 5, fontSize: 10, color: isLight ? '#1b5e20' : 'rgba(100,180,255,0.8)', background: 'none', border: '1px solid ' + (isLight ? 'rgba(27,94,32,0.35)' : 'rgba(100,180,255,0.25)'), borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontFamily: 'inherit' }}
                >Ask IVA →</button>
              );
              return (
                <div style={{ overflowY: 'auto', maxHeight: 'calc(50vh - 44px)' }}>
                  {/* Narrative observations — tappable to ask IVA */}
                  {profileInsights.narrative?.length > 0 && (
                    <div style={secStyle}>
                      <div style={secLabel}>Observations</div>
                      {profileInsights.narrative.map((n, i) => (
                        <div
                          key={i}
                          onClick={e => {
                            e.stopPropagation();
                            const followUps = profileInsights.narrative.filter((_, j) => j !== i);
                            if (followUps.length > 0) setCurrentSuggestions(followUps.map(q => `Tell me more: ${q}`));
                            setInsightsDismissed(true);
                            handleSend(`Tell me more about this: ${n}`);
                          }}
                          style={{ ...row, color: isLight ? '#1a1a1a' : 'rgba(220,235,255,0.7)', lineHeight: 1.5, cursor: 'pointer', borderRadius: 3, padding: '3px 2px', transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = hoverBg}
                          onMouseLeave={e => e.currentTarget.style.background = ''}
                        >· {n}</div>
                      ))}
                    </div>
                  )}
                  {/* Recurring charges */}
                  {profileInsights.subscriptions?.length > 0 && (
                    <div style={secStyle}>
                      <div style={secLabel}>Recurring Charges · ~${Math.round(profileInsights.subscriptions.reduce((s, r) => s + r.monthlyEst, 0))}/mo</div>
                      {profileInsights.subscriptions.map((sub, i) => (
                        <div key={i} style={row}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{sub.merchant}</span>
                          <span style={{ color: amountClr, whiteSpace: 'nowrap' }}>
                            ${sub.amount.toFixed(2)} <span style={{ opacity: 0.55, fontSize: 10 }}>{sub.frequency}</span>
                          </span>
                        </div>
                      ))}
                      {askBtn([
                        'Tell me about my recurring subscriptions',
                        'Which subscriptions should I consider canceling?',
                        'How much am I spending on subscriptions annually?',
                      ])}
                    </div>
                  )}
                  {/* Unusual charges */}
                  {profileInsights.anomalies?.length > 0 && (
                    <div style={secStyle}>
                      <div style={secLabel}>Unusual Charges</div>
                      {profileInsights.anomalies.map((a, i) => {
                        const txKey = `${a.date}|${a.description}`;
                        const fraudStatus = fraudReports[txKey];
                        const flagClr = fraudStatus === 'fraud' ? '#c00' : fraudStatus === 'ok' ? (isLight ? '#1b7a3d' : 'rgba(90,220,140,0.9)') : (isLight ? '#c1440e' : 'rgba(255,150,90,0.95)');
                        const rowClick = (e) => { e.stopPropagation(); onNavigate('transactionDetail', a); };
                        const borderClr = fraudStatus === 'fraud' ? '#c00' : fraudStatus === 'ok' ? 'rgba(60,200,100,0.9)' : 'rgba(255,120,60,0.9)';
                        const statusLabel = fraudStatus === 'fraud' ? '🚨 Reported as fraud' : fraudStatus === 'ok' ? '✓ Confirmed not fraud' : (a.firstSeen ? '⚠ New merchant' : `${a.multiple}× usual`);
                        return (
                          <div
                            key={i}
                            onClick={rowClick}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              background: a.firstSeen ? (isLight ? 'rgba(255,120,60,0.08)' : 'rgba(255,120,60,0.12)') : (isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)'),
                              borderLeft: `3px solid ${borderClr}`,
                              borderRadius: 6, padding: '7px 8px 7px 10px', margin: '4px 0',
                              cursor: 'pointer', transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = hoverBg}
                            onMouseLeave={e => e.currentTarget.style.background = a.firstSeen ? (isLight ? 'rgba(255,120,60,0.08)' : 'rgba(255,120,60,0.12)') : (isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)')}
                          >
                            {/* Left: merchant + status sub-label */}
                            <span style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
                              <span style={{ display: 'block', fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isLight ? '#111' : 'rgba(220,235,255,0.9)' }}>{a.description}</span>
                              <span style={{ display: 'block', fontSize: 10, fontWeight: 700, color: flagClr, marginTop: 2 }}>{statusLabel}</span>
                            </span>
                            {/* Right: amount + chevron */}
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, marginLeft: 8 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: isLight ? '#c1440e' : 'rgba(255,150,90,0.95)', whiteSpace: 'nowrap' }}>${a.amount.toFixed(2)}</span>
                              <span style={{ fontSize: 14, color: isLight ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.25)', lineHeight: 1 }}>›</span>
                            </span>
                          </div>
                        );
                      })}
                      {askBtn([
                        `Tell me about the unusual charge at ${profileInsights.anomalies[0].description}`,
                        'Are any of my recent unusual charges something I should dispute?',
                        'What were my biggest one-time charges this month?',
                      ])}
                    </div>
                  )}
                  {/* Budget pulse */}
                  {profileInsights.budgetPulse?.length > 0 && (
                    <div style={secStyle}>
                      <div style={secLabel}>Spending Shifts vs Usual</div>
                      {profileInsights.budgetPulse.map((b, i) => (
                        <div key={i} style={row}>
                          <span>{b.category}</span>
                          <span style={{ color: b.direction === 'up' ? 'rgba(255,150,90,0.9)' : 'rgba(90,220,140,0.9)', whiteSpace: 'nowrap' }}>
                            {b.direction === 'up' ? '↑' : '↓'} {Math.abs(b.changePct)}%
                            <span style={{ opacity: 0.5, fontSize: 10 }}> (${b.recent} vs ${b.priorMonthly}/mo)</span>
                          </span>
                        </div>
                      ))}
                      {askBtn([
                        `Why has my ${profileInsights.budgetPulse[0].category} spending ${profileInsights.budgetPulse[0].direction === 'up' ? 'increased' : 'decreased'} recently?`,
                        'Give me a full budget breakdown for the last 30 days',
                        'Where am I overspending compared to my usual habits?',
                      ])}
                    </div>
                  )}
                  {/* Spending digest — top categories last 30 days */}
                  {profileInsights.spendDigest?.totalSpend > 0 && (
                    <div style={secStyle}>
                      <div style={secLabel}>Where Your Money Went (30 days) · ${profileInsights.spendDigest.totalSpend.toLocaleString()}</div>
                      {profileInsights.spendDigest.byCategory.slice(0, 6).map((cat, i) => (
                        <div key={i} style={row}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{cat.category}</span>
                          <span style={{ whiteSpace: 'nowrap' }}>
                            ${cat.amount.toLocaleString()}
                            <span style={{ opacity: 0.5, fontSize: 10 }}> {cat.pct}%</span>
                            {cat.changePct !== null && (
                              <span style={{ marginLeft: 5, fontSize: 10, color: cat.changePct > 10 ? 'rgba(255,150,90,0.9)' : cat.changePct < -10 ? 'rgba(90,220,140,0.9)' : (isLight ? 'rgba(0,0,0,0.4)' : 'rgba(160,210,255,0.45)') }}>
                                {cat.changePct > 0 ? '↑' : '↓'}{Math.abs(cat.changePct)}%
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                      {askBtn([
                        'Where did most of my money go this month?',
                        'How does my spending compare to last month?',
                        'Which category should I cut back on first?',
                      ])}
                    </div>
                  )}
                  {/* Financial health score */}
                  {profileInsights.healthScore?.indicators?.length > 0 && (
                    <div style={secStyle}>
                      <div style={secLabel}>Financial Health · {profileInsights.healthScore.score}/{profileInsights.healthScore.maxScore} healthy</div>
                      {profileInsights.healthScore.indicators.map((ind, i) => (
                        <div key={i} style={{ ...row, fontSize: 11, alignItems: 'flex-start', padding: '3px 0' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                            <span style={{
                              width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                              background: ind.status === 'healthy' ? 'rgba(90,220,140,0.9)' : ind.status === 'coping' ? 'rgba(255,200,60,0.9)' : 'rgba(255,100,100,0.9)',
                            }} />
                            {ind.label}
                          </span>
                          <span style={{ opacity: 0.5, fontSize: 10, textAlign: 'right' }}>{ind.detail}</span>
                        </div>
                      ))}
                      {askBtn([
                        'How is my overall financial health?',
                        'What can I do to improve my financial health score?',
                        'Am I saving enough for emergencies?',
                      ])}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        );
      })()}

      <div className="scroll-content chat-content-bg" ref={chatScrollRef}>
        {showCarousel ? (
          <div
            className="chat-initial-center"
            onPointerDown={startLongPress}
            onPointerUp={cancelLongPress}
            onPointerLeave={cancelLongPress}
            onPointerCancel={cancelLongPress}
          >
            <div className="chat-carousel-container">
              {chatCarouselData.map(card => (
                <div key={card.id} className="chat-carousel-card">
                  <div className="carousel-icon">{card.icon}</div>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </div>
              ))}
            </div>
            {history.map((msg, idx) => (
              <ChatMessage
                key={msg.id}
                {...msg}
                onRetry={msg.failed ? () => handleRetry(msg.retryText, msg.id) : undefined}
                onPin={msg.isBot && !msg.failed ? onSaveToDashboard : undefined}
                isPinned={pinnedMessageIds.includes(msg.id)}
                onFeedback={handleFeedback}
                onAssumptionDecision={handleAssumptionDecision}
                sessionId={sessionId}
                onGoalCreated={handleGoalCreated}
                onNavigate={onNavigate}
                advisorRequest={precedingUserText(history, idx)}
                onBeginSwipe={beginSwipe}
                onSwipeAnswer={advanceSwipe}
                onBeginEMT={beginEMT}
                onBeginAgentEMT={beginAgentEMT}
                onEMTAnswer={advanceEMT}
                onAgentEMTAnswer={advanceAgentEMT}
                swipeActive={!!swipeState}
              />
            ))}
          </div>
        ) : (
          <>
            <div className="chat-timestamp">Jan 7, 3:19 PM</div>
            {history.map((msg, idx) => (
              <ChatMessage
                key={msg.id}
                {...msg}
                onRetry={msg.failed ? () => handleRetry(msg.retryText, msg.id) : undefined}
                onPin={msg.isBot && !msg.failed ? onSaveToDashboard : undefined}
                isPinned={pinnedMessageIds.includes(msg.id)}
                onFeedback={handleFeedback}
                onAssumptionDecision={handleAssumptionDecision}
                sessionId={sessionId}
                onGoalCreated={handleGoalCreated}
                onNavigate={onNavigate}
                advisorRequest={precedingUserText(history, idx)}
                onBeginSwipe={beginSwipe}
                onSwipeAnswer={advanceSwipe}
                onBeginEMT={beginEMT}
                onBeginAgentEMT={beginAgentEMT}
                onEMTAnswer={advanceEMT}
                onAgentEMTAnswer={advanceAgentEMT}
                swipeActive={!!swipeState}
              />
            ))}
          </>
        )}
        <div ref={chatEndRef} />
      </div>


      {simDate && (
        <div style={{ padding: '4px 12px', fontSize: 10, color: '#f59e0b', background: 'transparent', textAlign: 'center', letterSpacing: 0.3 }}>
          🗓 Simulating {simDate} — IVA is reasoning from this date
        </div>
      )}
      <div className="chat-input-area">
        <div className="suggestions-row" ref={suggestionsRef}>
          {currentSuggestions.map((s, i) => {
            // chatSuggestions accepts either plain strings (legacy) or
            // { text, descriptor } objects (current). text is what gets sent.
            const text = typeof s === 'string' ? s : (s?.text || '');
            return <ChatSuggestion key={i} text={text} onClick={() => handleSend(text)} />;
          })}
        </div>
        {isListening && (
          <div className="chat-voice-status" role="status" aria-live="polite">
            <span className="chat-voice-dot" /> Listening{transcript ? `: "${transcript}"` : '…'}
          </div>
        )}
        {isSpeaking && !isListening && (
          // Clickable pill — tapping cancels TTS playback so the user can
          // interrupt a long reply without waiting it out. Earlier builds
          // had this; it got dropped during the chat reskin and is back
          // here as a proper button (with keyboard + a11y semantics).
          <button
            type="button"
            className="chat-voice-status speaking"
            onClick={cancelSpeech}
            aria-label="Stop speaking"
            title="Tap to stop"
            style={{ cursor: 'pointer', border: 'none' }}
          >
            <span className="chat-voice-dot" /> Speaking… <span style={{ opacity: 0.7, marginLeft: 4 }}>(tap to stop)</span>
          </button>
        )}
        <div className="input-row">
          {voiceSupported && (
            <button
              className={`mic-button${(isListening || pendingListen) ? ' active' : ''}${micLongPressActive ? ' long-pressing' : ''}`}
              onPointerDown={startMicLongPress}
              onPointerUp={cancelMicLongPress}
              onPointerLeave={cancelMicLongPress}
              onPointerCancel={cancelMicLongPress}
              onClick={(e) => {
                // The long-press handler has already opened conversation mode
                // and consumed this gesture — don't toggle listening too.
                if (micLongPressFiredRef.current) {
                  micLongPressFiredRef.current = false;
                  e.preventDefault();
                  return;
                }
                if (isSpeaking) { cancelSpeech(); return; }
                // Show active state immediately (before SpeechRecognition confirms open).
                if (!isListening) {
                  setPendingListen(true);
                  setTimeout(() => setPendingListen(false), 600);
                }
                toggleListening();
              }}
              title={isListening ? 'Stop listening' : 'Speak (hold 3 s for conversation mode)'}
              aria-label={isListening ? 'Stop listening' : 'Speak (hold 3 s for conversation mode)'}
              aria-pressed={isListening}
            >
              {micLongPressActive
                ? <MdRecordVoiceOver />
                : isListening
                  ? <MdMicOff />
                  : <MdMic />}
            </button>
          )}
          <input
            type="text"
            placeholder={isListening ? 'Listening…' : 'Ask me something'}
            value={isListening && transcript ? transcript : inputText}
            onChange={(e) => { setInputText(e.target.value); if (isListening) setTranscript(e.target.value); }}
            onKeyPress={(e) => e.key === 'Enter' && handleSend(inputText)}
          />
          {isProcessing ? (
            <button className="send-button stop-button stop-button--thinking" onClick={handleStop} title="Stop">
              <MdStopCircle />
              <span className="thinking-ring" />
            </button>
          ) : (
            <button className="send-button" onClick={() => handleSend(inputText)}>
              <MdSend />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatScreen;
